import type { LifeSpace, Project, Task } from '@/src/renderer/lib/db/models'
import { LIFE_SPACES, type BackupTables } from './format'
import type { RestorePlan, TableOutcome } from './planRestore'

/**
 * **Vorschau vor dem Wiederherstellen** (#158, Entscheidung 16): was zurückkommt und was neu ist,
 * gegliedert nach Lebensbereich › Projekt › Aufgaben (Unteraufgaben eingerückt), dazu Ziele und
 * Gewohnheiten. „Schon da“ nur als Zahl.
 */

export type SummaryKind = 'projects' | 'sections' | 'tasks' | 'notes' | 'comments' | 'goals' | 'habits'
export const SUMMARY_KINDS: readonly SummaryKind[] = ['projects', 'sections', 'tasks', 'notes', 'comments', 'goals', 'habits']

/** Welche Tabellen in die Zahlen eingehen — Erinnerungen, Fokuszeit, Tags und Häkchen der Gewohnheiten reisen nur mit. */
const SUMMARY_TABLES: Record<SummaryKind, Array<keyof RestorePlan['outcome']>> = {
  projects: ['projects'],
  sections: ['sections'],
  tasks: ['tasks'],
  notes: ['canvasNotes'],
  comments: ['taskComments', 'projectComments'],
  goals: ['goals'],
  habits: ['habits'],
}

export interface PreviewTaskLine { id: string; title: string; depth: number }
export interface PreviewProjectGroup { id: string; name: string; tasks: PreviewTaskLine[] }
export interface PreviewLifeSpaceGroup {
  lifeSpace: LifeSpace
  projects: PreviewProjectGroup[]
  quickNotes: PreviewTaskLine[]
  goals: string[]
  habits: string[]
}
export interface PreviewPart {
  counts: Record<SummaryKind, number>
  total: number
  lifeSpaces: PreviewLifeSpaceGroup[]
}
export interface RestorePreviewModel {
  revived: PreviewPart
  added: PreviewPart
  existing: number
  skipped: number
  overwritesSettings: boolean
  /** Nichts zu schreiben, auch keine Einstellung. */
  nothingToDo: boolean
}

export function summarize(outcome: RestorePlan['outcome'], pick: (o: TableOutcome) => number): Record<SummaryKind, number> {
  return Object.fromEntries(SUMMARY_KINDS.map(kind => [kind, SUMMARY_TABLES[kind].reduce((sum, table) => sum + pick(outcome[table]), 0)])) as Record<SummaryKind, number>
}

const sumAll = (counts: Record<SummaryKind, number>) => SUMMARY_KINDS.reduce((sum, kind) => sum + counts[kind], 0)

export function buildRestorePreview(plan: RestorePlan, device: BackupTables): RestorePreviewModel {
  const projectById = new Map<string, Project>([...device.projects, ...plan.write.projects].map(p => [p.id, p]))
  const taskById = new Map<string, Task>([...device.tasks, ...plan.write.tasks].map(t => [t.id, t]))
  /** Die echte Tiefe — eine neue Unteraufgabe unter einer zurückgeholten Aufgabe steht trotzdem eingerückt. */
  const depthOf = (task: Task) => {
    let depth = 0
    const seen = new Set([task.id])
    for (let parent = task.parentTaskId ? taskById.get(task.parentTaskId) : undefined; parent && !seen.has(parent.id); parent = parent.parentTaskId ? taskById.get(parent.parentTaskId) : undefined) {
      seen.add(parent.id)
      depth++
    }
    return depth
  }
  const part = (pick: 'revived' | 'added'): PreviewPart => {
    const counts = summarize(plan.outcome, o => o[pick].length)
    const tasks = plan.outcome.tasks[pick] as Task[]
    const projects = plan.outcome.projects[pick] as Project[]
    const lifeSpaces = LIFE_SPACES.map(lifeSpace => {
      const inSpace = tasks.filter(t => t.lifeSpace === lifeSpace)
      const projectIds = new Set([
        ...projects.filter(p => p.lifeSpace === lifeSpace).map(p => p.id),
        ...inSpace.flatMap(t => (t.projectId ? [t.projectId] : [])),
      ])
      return {
        lifeSpace,
        projects: [...projectIds]
          .map(id => projectById.get(id))
          .filter((p): p is Project => !!p)
          .sort((a, b) => a.order - b.order)
          .map(p => ({ id: p.id, name: p.name, tasks: taskLines(inSpace.filter(t => t.projectId === p.id), depthOf) })),
        quickNotes: taskLines(inSpace.filter(t => !t.projectId), depthOf),
        goals: plan.outcome.goals[pick].filter(g => (g as { lifeSpace: LifeSpace }).lifeSpace === lifeSpace).map(g => (g as { title: string }).title),
        habits: plan.outcome.habits[pick].filter(h => (h as { lifeSpace: LifeSpace }).lifeSpace === lifeSpace).map(h => (h as { title: string }).title),
      }
    }).filter(group => group.projects.length + group.quickNotes.length + group.goals.length + group.habits.length > 0)
    return { counts, total: sumAll(counts), lifeSpaces }
  }
  const existing = sumAll(summarize(plan.outcome, o => o.existing))
  const skipped = sumAll(summarize(plan.outcome, o => o.skipped))
  const nothingToDo = Object.values(plan.write).every(rows => rows.length === 0)
  return { revived: part('revived'), added: part('added'), existing, skipped, overwritesSettings: plan.overwritesSettings, nothingToDo }
}

/** Aufgaben als Baum innerhalb dieser Gruppe — Kinder hinter ihrem Elternteil, eingerückt nach ihrer echten Tiefe. */
function taskLines(tasks: Task[], depthOf: (task: Task) => number): PreviewTaskLine[] {
  const ids = new Set(tasks.map(t => t.id))
  const children = new Map<string | null, Task[]>()
  for (const task of tasks) {
    const parent = task.parentTaskId && ids.has(task.parentTaskId) ? task.parentTaskId : null
    children.set(parent, [...(children.get(parent) ?? []), task])
  }
  const lines: PreviewTaskLine[] = []
  const walk = (parent: string | null) => {
    for (const task of [...(children.get(parent) ?? [])].sort((a, b) => a.order - b.order)) {
      lines.push({ id: task.id, title: task.title, depth: depthOf(task) })
      walk(task.id)
    }
  }
  walk(null)
  return lines
}
